import apiClient from '.';
import { setLoading } from '@/plugins/loading';

const useBatchDeleteMock = process.env.VUE_APP_USE_BATCH_DELETE_MOCK === 'true';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const buildBatchDeleteMockResponse = isCheck => {
    if (isCheck) {
        return {
            status: 200,
            data: {
                valids: [
                    {
                        username: 'demo.user.1',
                        account_level: 'Employee',
                        club_name: 'theBB',
                        station: 'bb3',
                    },
                    {
                        username: 'demo.user.2',
                        account_level: 'Shop',
                        club_name: 'theBB',
                        station: 'bb3',
                    },
                ],
                errors: [],
                success_count: 0,
            },
        };
    }

    return {
        status: 200,
        data: {
            valids: [],
            errors: [],
            success_count: 2,
        },
    };
};

export const signin = async data => {
    try {
        setLoading(true);
        const response = await apiClient.post('/users/account/sign-in', data, {
            skipAuthHandling: true,
            skipAuthorization: true,
        });
        return response.data;
    } catch (error) {
        if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
        ) {
            return error.response.data;
        }
    } finally {
        setLoading(false);
    }
};

export const userSelf = async () => {
    try {
        setLoading(true);
        const response = await apiClient.get('/users/self');
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchUsersFromAPI = async params => {
    try {
        const response = await apiClient.get('/users', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchShopsFromAPI = async params => {
    try {
        setLoading(true);

        const response = await apiClient.get('/users/shops', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const exportUsersFromAPI = async (params, type) => {
    try {
        if (localStorage.getItem('locale') !== undefined) {
            params.output_language = localStorage.getItem('locale');
        }
        const response = await apiClient.get(`/users/export/${type}`, {
            params,
            responseType: 'blob',
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchConfig = async () => {
    try {
        setLoading(true);
        const response = await apiClient.get('/users/configs');
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchShopListConfig = async () => {
    try {
        setLoading(true);
        const response = await apiClient.get('/users/shops/configs');
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchCreateUserConfig = async (accountLevel = 'account') => {
    try {
        setLoading(true);
        const params = {
            account_level: accountLevel,
        };
        const response = await apiClient.get('/users/create-account/configs', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchEditUserConfig = async accountLevel => {
    try {
        setLoading(true);
        if (accountLevel.toLowerCase() === 'shop') {
            accountLevel = 'Account';
        }
        const params = {
            account_level: accountLevel,
        };
        const response = await apiClient.get('/users/edit-account/configs', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchCreateShopConfig = async () => {
    try {
        setLoading(true);
        const params = {
            account_level: 'Shop',
        };
        const response = await apiClient.get('/users/create-account/configs', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

/**
 * Update the current user's time zone.
 * @param {string} timeZone IANA time zone identifier
 */
export const updateUserTimeZone = async timeZone => {
    try {
        setLoading(true);
        const response = await apiClient.patch('/users/self', {
            time_zone: timeZone,
        });
        return response.data;
    } catch (error) {
        console.error('Failed to update user time zone', error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchEditShopConfig = async () => {
    try {
        setLoading(true);
        const params = {
            account_level: 'Shop',
        };
        const response = await apiClient.get('/users/edit-account/configs', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchCreateShopManagerConfig = async () => {
    try {
        setLoading(true);
        const params = {
            account_level: 'Shop_Manager',
        };
        const response = await apiClient.get('/users/create-account/configs', {
            params,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchStations = async (params = {}) => {
    try {
        setLoading(true);
        const response = await apiClient.get('/users/stations', {
            params,
            skip403Handling: true,
        });
        return response.data;
    } catch (error) {
        // 不處理403錯誤，讓呼叫端自行判斷
        if (error.response && error.response.status === 403) {
            return { error: 'forbidden', status: 403 };
        }
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchClubs = async (params = {}) => {
    try {
        // 這裡不啟用全局 loading，只做角色判斷用
        const response = await apiClient.get('/users/clubs', {
            params,
            skip403Handling: true,
        });
        return response.data;
    } catch (error) {
        // 不處理403錯誤，讓呼叫端自行判斷
        if (error.response && error.response.status === 403) {
            return { error: 'forbidden', status: 403 };
        }
        console.error(error);
        throw error;
    }
};

export const checkInput = async (url, value) => {
    try {
        const encodedValue = encodeURIComponent(value);
        const response = await apiClient.get(url + encodedValue, {
            skipErrorPopup: true,
        });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const getOptions = async (url, params) => {
    try {
        setLoading(true);

        const response = await apiClient.get(url, { params });
        const data = response.data;
        if (data && data.length > 0) {
            // extract name from each object and turn into a string array
            return data;
        }
        return [];
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const checkClubExist = async clubId => {
    const response = await apiClient.get(`users/clubs?search=${clubId}`);
    return response.data.length > 0;
};

export const createClub = async clubId => {
    setLoading(true);
    try {
        const response = await apiClient.post('users/clubs', { name: clubId });
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const createUser = async data => {
    setLoading(true);
    try {
        const response = await apiClient.post('users/create-account', data);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const createShop = async data => {
    setLoading(true);
    try {
        const response = await apiClient.post('users/shops', data);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const editUser = async (id, data) => {
    setLoading(true);
    try {
        const response = await apiClient.patch(`users/${id}`, data);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const editShop = async (id, data) => {
    setLoading(true);
    try {
        const response = await apiClient.patch(`users/shops/${id}`, data);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const getUser = async id => {
    try {
        const response = await apiClient.get(`users/${id}`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const getShop = async id => {
    try {
        const response = await apiClient.get(`users/shops/${id}`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const sendResetPasswordLink = async email => {
    try {
        const response = await apiClient.post(
            'users/verification/request-password-reset',
            { email }
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const verifyUserVerificationToken = async token => {
    try {
        const response = await apiClient.get(
            `users/verification/validate-user-token/${token}`
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const verifyResetPasswordToken = async token => {
    try {
        const response = await apiClient.get(
            `users/verification/validate-reset-password-token/${token}`
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const verifyUser = async (token, newPassword) => {
    try {
        const response = await apiClient.post('users/verify-user', {
            token: token,
            new_password: newPassword,
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const resetPassword = async (token, newPassword) => {
    try {
        const response = await apiClient.post('users/reset-password', {
            token: token,
            new_password: newPassword,
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const continuePassword = async () => {
    try {
        const response = await apiClient.post(`users/password-continue`);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const changePassword = async (oldPassword, newPassword) => {
    try {
        const response = await apiClient.post('users/change-password', {
            old_password: oldPassword,
            new_password: newPassword,
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const checkPasswordRule = async password => {
    setLoading(true);
    try {
        const response = await apiClient.post(
            'users/account/check-password-rule',
            { password },
            {
                skipErrorPopup: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const resendVerificationEmail = async email => {
    try {
        const response = await apiClient.post(
            'users/verification/request-user-verification-resend',
            {
                email: email,
                output_language: localStorage.getItem('locale') || 'en',
            }
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const deleteUser = async id => {
    try {
        const response = await apiClient.delete(`users/${id}`);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const deleteShop = async id => {
    try {
        const response = await apiClient.delete(`users/shops/${id}`);
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const resetUserPassword = async username => {
    try {
        const response = await apiClient.post('users/reset-user-password', {
            username: username,
            output_language: localStorage.getItem('locale') || 'en',
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const checkBatchCreateUsersPermission = async () => {
    setLoading(true);
    try {
        const response = await apiClient.get(
            'users/batch-create/check-permission'
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchBatchCreateConfig = async () => {
    setLoading(true);
    try {
        const response = await apiClient.get('users/batch-create-config');
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const batchCreateUsers = async formData => {
    setLoading(true);
    try {
        if (localStorage.getItem('locale') !== undefined) {
            formData.append('output_language', localStorage.getItem('locale'));
        }
        const response = await apiClient.post('users/batch-create', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            skipErrorPopup: true,
        });
        return response;
    } catch (error) {
        if (
            error.response &&
            (error.response.status === 400 || error.response.status === 500)
        ) {
            return error.response;
        } else {
            throw error;
        }
    } finally {
        setLoading(false);
    }
};

export const checkBatchDeleteUsersPermission = async () => {
    setLoading(true);
    try {
        if (useBatchDeleteMock) {
            await wait(200);
            return {
                status: 200,
                data: { allowed: true },
            };
        }
        const response = await apiClient.get(
            'users/batch-delete/check-permission'
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const fetchBatchDeleteConfig = async () => {
    setLoading(true);
    try {
        if (useBatchDeleteMock) {
            await wait(200);
            return [
                {
                    field: 'username',
                    field_name: 'users_create__username',
                    required: 'true',
                    leading_zero: 'false',
                },
                {
                    field: 'account_level',
                    field_name: 'users_create__account_level',
                    required: 'true',
                    leading_zero: 'false',
                },
                {
                    field: 'club_name',
                    field_name: 'users_create__club_name',
                    required: 'true',
                    leading_zero: 'true',
                },
                {
                    field: 'station',
                    field_name: 'users_create__station',
                    required: 'true',
                    leading_zero: 'true',
                },
            ];
        }
        const response = await apiClient.get('users/batch-delete-config');
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const batchDeleteUsers = async formData => {
    setLoading(true);
    try {
        if (useBatchDeleteMock) {
            await wait(350);
            const isCheck = String(formData.get('is_check')) === 'true';
            return buildBatchDeleteMockResponse(isCheck);
        }
        if (localStorage.getItem('locale') !== undefined) {
            formData.append('output_language', localStorage.getItem('locale'));
        }
        const response = await apiClient.post('users/batch-delete', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            skipErrorPopup: true,
        });
        return response;
    } catch (error) {
        if (
            error.response &&
            (error.response.status === 400 || error.response.status === 500)
        ) {
            return error.response;
        } else {
            throw error;
        }
    } finally {
        setLoading(false);
    }
};

export const registerUser = async data => {
    try {
        setLoading(true);
        if (localStorage.getItem('locale') !== undefined) {
            data.output_language = localStorage.getItem('locale');
        }
        const response = await apiClient.post('users/register-account', data, {
            skipAuthHandling: true,
            skipAuthorization: true,
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const inviteUserToClub = async email => {
    try {
        setLoading(true);
        const response = await apiClient.post(
            'users/clubs/invite-club-member',
            {
                email: email,
                output_language: localStorage.getItem('locale') || 'en',
            }
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const clubInvitation = async token => {
    try {
        setLoading(true);
        const response = await apiClient.post(
            `users/clubs/invitation/${token}`
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const kickUserFromClub = async user_id => {
    try {
        setLoading(true);
        const response = await apiClient.post(
            'users/clubs/remove-club-member',
            {
                user_id: user_id,
            }
        );
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};

export const getBatchCreateTemplate = async () => {
    try {
        setLoading(true);
        const response = await apiClient.get('users/batch-create-template', {
            params: {
                output_language: localStorage.getItem('locale'),
            },
            responseType: 'blob',
            skipAuthHandling: true,
        });
        return response;
    } catch (error) {
        console.error(error);
        throw error;
    } finally {
        setLoading(false);
    }
};
